import { apiClient } from '../../../services/apiClient';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  switch (method) {
    case 'GET': return apiClient.get<T>(path);
    case 'POST': return apiClient.post<T>(path, body);
    case 'PATCH': return apiClient.patch<T>(path, body);
    case 'PUT': return apiClient.put<T>(path, body);
    case 'DELETE': return apiClient.delete<T>(path);
    default: throw new Error(`Unsupported method: ${method}`);
  }
}

const requestFormData = <T>(path: string, formData: FormData): Promise<T> =>
  apiClient.upload<T>(path, formData);

// ── Organization ──

export interface AddressDto {
  streetAddress?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
}

export interface OrganizationDto {
  id: string;
  name: string;
  legalName?: string;
  address?: AddressDto;
  taxIdentifier?: string;
  taxIdentifierType?: string;
  contactEmail?: string;
  contactPhone?: string;
  websiteUrl?: string;
  logoUrl?: string;
  status: string;
  rowVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  legalName?: string;
  address?: AddressDto;
  taxIdentifier?: string;
  taxIdentifierType?: string;
  contactEmail?: string;
  contactPhone?: string;
  websiteUrl?: string;
  rowVersion?: number;
}

export const getOrganization = () =>
  request<OrganizationDto>('GET', '/api/v1/organizations/me');

export const updateOrganization = (data: UpdateOrganizationRequest) =>
  request<OrganizationDto>('PATCH', '/api/v1/organizations/me', data);

export const uploadLogo = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return requestFormData<{ logoUrl: string }>('/api/v1/organizations/me/logo', formData);
};

export const deleteLogo = () =>
  request<void>('DELETE', '/api/v1/organizations/me/logo');

// ── Locations ──

export interface LocationDto {
  id: string;
  name: string;
  venueType: string;
  address?: AddressDto;
  timezone?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLocationRequest {
  name: string;
  venueType?: string;
  address?: AddressDto;
  timezone?: string;
}

export interface UpdateLocationRequest {
  name?: string;
  venueType?: string;
  address?: AddressDto;
  timezone?: string;
}

export const getLocations = () =>
  request<LocationDto[]>('GET', '/api/v1/organizations/me/locations');

export const getLocation = (id: string) =>
  request<LocationDto>('GET', `/api/v1/organizations/me/locations/${id}`);

export const createLocation = (data: CreateLocationRequest) =>
  request<LocationDto>('POST', '/api/v1/organizations/me/locations', data);

export const updateLocation = (id: string, data: UpdateLocationRequest) =>
  request<LocationDto>('PATCH', `/api/v1/organizations/me/locations/${id}`, data);

export const deleteLocation = (id: string) =>
  request<void>('DELETE', `/api/v1/organizations/me/locations/${id}`);

// ── Users ──

export interface LocationSummaryDto {
  id: string;
  name: string;
}

export interface OrganizationUserDto {
  id: string;
  email: string;
  fullName?: string;
  role: string;
  locationAccess: LocationSummaryDto[];
  createdAt: string;
}

export interface UpdateUserRequest {
  role?: string;
  locationIds?: string[];
}

export const getUsers = () =>
  request<OrganizationUserDto[]>('GET', '/api/v1/organizations/me/users');

export const updateUser = (userId: string, data: UpdateUserRequest) =>
  request<OrganizationUserDto>('PATCH', `/api/v1/organizations/me/users/${userId}`, data);

export const removeUser = (userId: string) =>
  request<void>('DELETE', `/api/v1/organizations/me/users/${userId}`);

// ── Invitations ──

export interface InvitationDto {
  id: string;
  email: string;
  role: string;
  locationAccess: LocationSummaryDto[];
  status: string;
  invitedBy?: string;
  expiresAt: string;
  createdAt: string;
}

export interface InviteUserRequest {
  email: string;
  role: string;
  locationIds?: string[];
}

export const getInvitations = () =>
  request<InvitationDto[]>('GET', '/api/v1/organizations/me/invitations');

export const inviteUser = (data: InviteUserRequest) =>
  request<InvitationDto>('POST', '/api/v1/organizations/me/invitations', data);

export const revokeInvitation = (id: string) =>
  request<void>('DELETE', `/api/v1/organizations/me/invitations/${id}`);

export const acceptInvitation = (token: string) =>
  request<{ organization: OrganizationDto; role: string }>('POST', '/api/v1/invitations/accept', { token });

// ── Audit Log ──

// ── Opening Hours ──

export interface OpeningHoursEntryDto {
  id?: string;
  dayOfWeek: number;
  openTime?: string;
  closeTime?: string;
  isClosed: boolean;
}

export interface SpecialDateHoursDto {
  id?: string;
  date: string;
  openTime?: string;
  closeTime?: string;
  isClosed: boolean;
  label?: string;
  createdAt?: string;
}

export interface OpeningHoursResponseDto {
  weeklySchedule: OpeningHoursEntryDto[];
  specialDates: SpecialDateHoursDto[];
}

export interface SetOpeningHoursRequest {
  entries: OpeningHoursEntryDto[];
}

export interface CreateSpecialDateRequest {
  date: string;
  openTime?: string;
  closeTime?: string;
  isClosed: boolean;
  label?: string;
}

export interface UpdateSpecialDateRequest {
  openTime?: string;
  closeTime?: string;
  isClosed?: boolean;
  label?: string;
}

export const getOpeningHours = (locationId: string) =>
  request<OpeningHoursResponseDto>('GET', `/api/v1/organizations/me/locations/${locationId}/opening-hours`);

export const setOpeningHours = (locationId: string, data: SetOpeningHoursRequest) =>
  request<OpeningHoursResponseDto>('PUT', `/api/v1/organizations/me/locations/${locationId}/opening-hours`, data);

export const getSpecialDates = (locationId: string) =>
  request<SpecialDateHoursDto[]>('GET', `/api/v1/organizations/me/locations/${locationId}/special-dates`);

export const createSpecialDate = (locationId: string, data: CreateSpecialDateRequest) =>
  request<SpecialDateHoursDto>('POST', `/api/v1/organizations/me/locations/${locationId}/special-dates`, data);

export const updateSpecialDate = (locationId: string, specialDateId: string, data: UpdateSpecialDateRequest) =>
  request<SpecialDateHoursDto>('PUT', `/api/v1/organizations/me/locations/${locationId}/special-dates/${specialDateId}`, data);

export const deleteSpecialDate = (locationId: string, specialDateId: string) =>
  request<void>('DELETE', `/api/v1/organizations/me/locations/${locationId}/special-dates/${specialDateId}`);

// ── Audit Log ──

export interface AuditLogEntryDto {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  userName?: string;
  createdAt: string;
}

export interface AuditLogPageDto {
  items: AuditLogEntryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export const getAuditLogs = (params?: {
  entityType?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}) => {
  const searchParams = new URLSearchParams();
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  const qs = searchParams.toString();
  return request<AuditLogPageDto>('GET', `/api/v1/organizations/me/audit-logs${qs ? `?${qs}` : ''}`);
};
